import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Phone, Scissors, Droplets, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { normalizePhone, isValidGrPhone, isValidEmail } from "@/lib/phone";
import { getSlotsForDate } from "@/lib/hours";
import { loadPlanPrices } from "@/lib/plans";

const SW_PLANS = [
  { key: "small", label: "Μικρόσωμο", price: 10, min: 16 },
  { key: "medium", label: "Μεσαίο", price: 14, min: 22 },
  { key: "large", label: "Μεγάλο", price: 20, min: 32 },
  { key: "giant", label: "Γίγαντας", price: 24, min: 40 },
];

export const PhoneBookingDialog = ({ open, onOpenChange, onCreated }) => {
  const { t } = useLang();
  const p = t.admin.phone_book;
  const [tab, setTab] = useState("grooming");
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("phone"); // phone | other
  const [custMode, setCustMode] = useState("new"); // new | existing
  const [sq, setSq] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selCust, setSelCust] = useState(null);

  const [g, setG] = useState({ customer_name: "", surname: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "medium", service: "", preferred_date: "", preferred_time: "" });
  const [gSlots, setGSlots] = useState([]);
  const [sw, setSw] = useState({ customer_name: "", surname: "", phone: "", email: "", plan: "medium", nagayu: false, date: "", time: "" });
  const [planPrices, setPlanPrices] = useState(null);
  useEffect(() => { if (open) loadPlanPrices().then(setPlanPrices); }, [open]);

  // Live customer search (name / surname / phone / email / breed / dog name)
  useEffect(() => {
    if (custMode !== "existing" || selCust || sq.trim().length < 2) { setResults([]); return; }
    const tmr = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/admin/customers/search?q=${encodeURIComponent(sq.trim())}`);
        setResults(r.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(tmr);
  }, [sq, custMode, selCust]);

  const pickCustomer = (c) => {
    setSelCust(c);
    setResults([]);
    setSq("");
    setG((prev) => ({
      ...prev,
      customer_name: c.first_name || "", surname: c.surname || "",
      phone: c.phone || "", email: c.email || "",
      pet_name: c.pet_name || prev.pet_name, pet_breed: c.pet_breed || prev.pet_breed,
      pet_size: c.pet_size || prev.pet_size,
    }));
    setSw((prev) => ({ ...prev, customer_name: c.first_name || "", surname: c.surname || "", phone: c.phone || "", email: c.email || "" }));
  };
  const emptyG = { customer_name: "", surname: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "medium", service: "", preferred_date: "", preferred_time: "" };
  const emptySw = { customer_name: "", surname: "", phone: "", email: "", plan: "medium", nagayu: false, date: "", time: "" };
  const clearCustomer = () => {
    setSelCust(null);
    // Changing the customer context must give a truly empty form, not unlocked-but-populated fields
    setG((prev) => ({ ...emptyG, preferred_date: prev.preferred_date, preferred_time: prev.preferred_time }));
    setSw((prev) => ({ ...emptySw, date: prev.date, time: prev.time }));
  };
  const locked = custMode === "existing" && !!selCust;

  useEffect(() => {
    if (!open) { setCustMode("new"); setSelCust(null); setSq(""); setResults([]); setSource("phone"); }
  }, [open]);

  const loadGSlots = useCallback(async (date) => {
    if (!date) { setGSlots([]); return; }
    try {
      const res = await api.get(`/grooming/availability?date=${date}`);
      setGSlots(res.data.available || []);
    } catch { setGSlots([]); }
  }, []);

  useEffect(() => { if (open && g.preferred_date) loadGSlots(g.preferred_date); }, [open, g.preferred_date, loadGSlots]);

  const swPlanBase = SW_PLANS.find((x) => x.key === sw.plan) || SW_PLANS[1];
  const swPlan = { ...swPlanBase, price: planPrices?.[swPlanBase.key] ?? swPlanBase.price };
  const swSlots = sw.date ? getSlotsForDate(new Date(sw.date + "T00:00:00")) : [];
  const swTotal = swPlan.price + (sw.nagayu ? 10 : 0);

  const submitGrooming = async () => {
    if (!g.customer_name || !g.pet_name || !g.pet_breed || !g.preferred_date || !g.preferred_time) { toast.error(p.required); return; }
    if (!isValidGrPhone(g.phone)) { toast.error(p.phone_error); return; }
    if (g.email && !isValidEmail(g.email)) { toast.error(p.email_error); return; }
    setSaving(true);
    try {
      const { surname, ...rest } = g;
      await api.post("/admin/phone-bookings/grooming", {
        ...rest,
        customer_name: surname.trim() ? `${g.customer_name.trim()} ${surname.trim()}` : g.customer_name.trim(),
        first_name: g.customer_name.trim(),
        email: g.email || null,
        service: g.service || null,
        source,
      });
      toast.success(p.saved);
      onCreated?.();
      onOpenChange(false);
      setG({ customer_name: "", surname: "", phone: "", email: "", pet_name: "", pet_breed: "", pet_size: "medium", service: "", preferred_date: "", preferred_time: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || p.error);
    } finally { setSaving(false); }
  };

  const submitSelfwash = async () => {
    if (!sw.customer_name || !sw.date || !sw.time) { toast.error(p.required); return; }
    if (!isValidGrPhone(sw.phone)) { toast.error(p.phone_error); return; }
    if (sw.email && !isValidEmail(sw.email)) { toast.error(p.email_error); return; }
    setSaving(true);
    try {
      await api.post("/admin/phone-bookings/selfwash", {
        customer_name: sw.surname.trim() ? `${sw.customer_name.trim()} ${sw.surname.trim()}` : sw.customer_name.trim(),
        first_name: sw.customer_name.trim(),
        phone: sw.phone, email: sw.email || null,
        plan: sw.plan, plan_price: swPlan.price, plan_duration: swPlan.min, nagayu: sw.nagayu,
        date: sw.date, time: sw.time,
        source,
      });
      toast.success(p.saved);
      onCreated?.();
      onOpenChange(false);
      setSw({ customer_name: "", surname: "", phone: "", email: "", plan: "medium", nagayu: false, date: "", time: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || p.error);
    } finally { setSaving(false); }
  };

  const field = "rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11";
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-cream max-w-lg rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="phone-booking-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <Phone className="h-5 w-5" /> {p.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">{p.subtitle}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          {/* Type tabs */}
          <div className="grid grid-cols-2 gap-2 rounded-full bg-white p-1 border border-brand/10" role="tablist">
            <button
              onClick={() => setTab("grooming")}
              className={`rounded-full h-10 font-bold text-sm transition-all ${tab === "grooming" ? "bg-brand text-brand-cream shadow-sm" : "text-brand-ink/60 hover:text-brand"}`}
              data-testid="pb-tab-grooming"
            >
              <Scissors className="h-4 w-4 inline mr-1.5" /> {p.tab_grooming}
            </button>
            <button
              onClick={() => setTab("selfwash")}
              className={`rounded-full h-10 font-bold text-sm transition-all ${tab === "selfwash" ? "bg-bamboo text-brand-cream shadow-sm" : "text-brand-ink/60 hover:text-bamboo-deep"}`}
              data-testid="pb-tab-selfwash"
            >
              <Droplets className="h-4 w-4 inline mr-1.5" /> {p.tab_selfwash}
            </button>
          </div>

          {/* Source choice */}
          <div>
            <p className="text-xs font-bold text-brand-ink/60 mb-1.5">{p.source_label}</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSource("phone")} className={`rounded-xl h-10 font-bold text-sm border transition-all ${source === "phone" ? "bg-brand text-brand-cream border-brand" : "bg-white text-brand-ink/60 border-brand/15 hover:border-brand/40"}`} data-testid="pb-source-phone">
                {p.source_phone}
              </button>
              <button onClick={() => setSource("other")} className={`rounded-xl h-10 font-bold text-sm border transition-all ${source === "other" ? "bg-brand text-brand-cream border-brand" : "bg-white text-brand-ink/60 border-brand/15 hover:border-brand/40"}`} data-testid="pb-source-other">
                {p.source_other}
              </button>
            </div>
          </div>

          {/* Customer mode */}
          <div>
            <p className="text-xs font-bold text-brand-ink/60 mb-1.5">{p.customer_label}</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setCustMode("existing")} className={`rounded-xl h-10 font-bold text-sm border transition-all ${custMode === "existing" ? "bg-bamboo text-brand-cream border-bamboo" : "bg-white text-brand-ink/60 border-brand/15 hover:border-brand/40"}`} data-testid="pb-cust-existing">
                {p.customer_existing}
              </button>
              <button onClick={() => { setCustMode("new"); clearCustomer(); }} className={`rounded-xl h-10 font-bold text-sm border transition-all ${custMode === "new" ? "bg-bamboo text-brand-cream border-bamboo" : "bg-white text-brand-ink/60 border-brand/15 hover:border-brand/40"}`} data-testid="pb-cust-new">
                {p.customer_new}
              </button>
            </div>
          </div>

          {/* Existing customer search */}
          {custMode === "existing" && !selCust && (
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
              <Input value={sq} onChange={(e) => setSq(e.target.value)} placeholder={p.search_ph} className={`${field} pl-10`} data-testid="pb-cust-search" />
              {(results.length > 0 || (sq.trim().length >= 2 && !searching)) && (
                <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-brand/15 bg-white shadow-lg max-h-56 overflow-y-auto" data-testid="pb-cust-results">
                  {results.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-brand-ink/50" data-testid="pb-cust-empty">{p.search_empty}</p>
                  ) : (
                    results.map((c, i) => (
                      <button key={i} onClick={() => pickCustomer(c)} className="w-full text-left px-4 py-2.5 hover:bg-brand-soft/40 border-b border-brand/5 last:border-0" data-testid={`pb-cust-result-${i}`}>
                        <span className="block font-bold text-brand-ink text-sm">{c.name || c.phone}</span>
                        <span className="block text-xs text-brand-ink/50">
                          {[c.phone, c.email, (c.pets || []).join(", "), (c.breeds || []).join(", ")].filter(Boolean).join(" · ")}
                          {c.bookings > 0 ? ` · ${c.bookings} ραντεβού` : ""}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {locked && (
            <div className="flex items-center justify-between rounded-xl bg-bamboo-soft/60 border border-bamboo/20 px-3.5 py-2.5" data-testid="pb-cust-selected">
              <div className="min-w-0">
                <p className="text-sm font-bold text-brand-ink truncate">{selCust.name || selCust.phone}</p>
                <p className="text-[11px] text-brand-ink/55">{p.locked_hint}</p>
              </div>
              <button onClick={clearCustomer} className="text-xs font-bold text-brand hover:text-brand-hover shrink-0 ml-3" data-testid="pb-cust-change">
                {p.change_customer}
              </button>
            </div>
          )}

          {tab === "grooming" && (
            <div className="space-y-3" data-testid="pb-form-grooming">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{p.name} *</Label><Input value={g.customer_name} onChange={(e) => setG({ ...g, customer_name: e.target.value })} disabled={locked} className={field} data-testid="pb-g-name" /></div>
                <div><Label>{p.surname}</Label><Input value={g.surname} onChange={(e) => setG({ ...g, surname: e.target.value })} disabled={locked} className={field} data-testid="pb-g-surname" /></div>
                <div>
                  <Label>{p.phone} *</Label>
                  <Input value={g.phone} onChange={(e) => setG({ ...g, phone: normalizePhone(e.target.value).slice(0, 10) })} disabled={locked} inputMode="tel" maxLength={10} placeholder="69XXXXXXXX" className={field} data-testid="pb-g-phone" />
                </div>
              </div>
              <div><Label>{p.email}</Label><Input type="email" value={g.email} onChange={(e) => setG({ ...g, email: e.target.value })} disabled={locked} className={field} data-testid="pb-g-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{p.pet_name} *</Label><Input value={g.pet_name} onChange={(e) => setG({ ...g, pet_name: e.target.value })} disabled={locked} className={field} data-testid="pb-g-petname" /></div>
                <div><Label>{p.pet_breed} *</Label><Input value={g.pet_breed} onChange={(e) => setG({ ...g, pet_breed: e.target.value })} disabled={locked} className={field} data-testid="pb-g-breed" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{p.pet_size}</Label>
                  <Select value={g.pet_size} onValueChange={(v) => setG({ ...g, pet_size: v })}>
                    <SelectTrigger className={field}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["small","medium","large","giant"].map((s) => <SelectItem key={s} value={s}>{p.sizes[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{p.service}</Label><Input value={g.service} onChange={(e) => setG({ ...g, service: e.target.value })} placeholder={p.service_placeholder} className={field} data-testid="pb-g-service" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{p.date} *</Label><Input type="date" min={today} value={g.preferred_date} onChange={(e) => setG({ ...g, preferred_date: e.target.value })} className={field} data-testid="pb-g-date" /></div>
                <div>
                  <Label>{p.time} *</Label>
                  <Input type="time" value={g.preferred_time} onChange={(e) => setG({ ...g, preferred_time: e.target.value })} className={field} data-testid="pb-g-time" />
                </div>
              </div>
              <Button onClick={submitGrooming} disabled={saving} className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11 mt-2" data-testid="pb-g-submit">
                <Save className="h-4 w-4 mr-2" /> {saving ? p.saving : p.save_grooming}
              </Button>
            </div>
          )}

          {tab === "selfwash" && (
            <div className="space-y-3" data-testid="pb-form-selfwash">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{p.name} *</Label><Input value={sw.customer_name} onChange={(e) => setSw({ ...sw, customer_name: e.target.value })} disabled={locked} className={field} data-testid="pb-sw-name" /></div>
                <div><Label>{p.surname}</Label><Input value={sw.surname} onChange={(e) => setSw({ ...sw, surname: e.target.value })} disabled={locked} className={field} data-testid="pb-sw-surname" /></div>
                <div>
                  <Label>{p.phone} *</Label>
                  <Input value={sw.phone} onChange={(e) => setSw({ ...sw, phone: normalizePhone(e.target.value).slice(0, 10) })} disabled={locked} inputMode="tel" maxLength={10} placeholder="69XXXXXXXX" className={field} data-testid="pb-sw-phone" />
                </div>
              </div>
              <div><Label>{p.email}</Label><Input type="email" value={sw.email} onChange={(e) => setSw({ ...sw, email: e.target.value })} disabled={locked} className={field} data-testid="pb-sw-email" /></div>
              <div>
                <Label>{p.plan} *</Label>
                <Select value={sw.plan} onValueChange={(v) => setSw({ ...sw, plan: v })}>
                  <SelectTrigger className={field} data-testid="pb-sw-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SW_PLANS.map((x) => <SelectItem key={x.key} value={x.key}>{x.label} · {x.min}′ · €{x.price}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setSw({ ...sw, nagayu: !sw.nagayu })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSw({ ...sw, nagayu: !sw.nagayu }); } }}
                className={`w-full text-left rounded-xl border p-3 flex items-center justify-between transition-colors cursor-pointer ${sw.nagayu ? "border-bamboo bg-bamboo-soft" : "border-brand/15 bg-white"}`}
                data-testid="pb-sw-nagayu-toggle"
              >
                <span className="text-sm font-bold text-brand-ink">Nagayu CO₂ Spa (+10€)</span>
                <Switch checked={sw.nagayu} onCheckedChange={(v) => setSw({ ...sw, nagayu: v })} onClick={(e) => e.stopPropagation()} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{p.date} *</Label><Input type="date" min={today} value={sw.date} onChange={(e) => setSw({ ...sw, date: e.target.value })} className={field} data-testid="pb-sw-date" /></div>
                <div>
                  <Label>{p.time} *</Label>
                  <Input type="time" value={sw.time} onChange={(e) => setSw({ ...sw, time: e.target.value })} className={field} data-testid="pb-sw-time" />
                </div>
              </div>
              <div className="rounded-xl bg-brand-soft/50 border border-brand/10 p-3 text-center text-sm font-bold text-brand-ink" data-testid="pb-sw-total">
                {p.total}: <span className="font-heading text-lg">€{swTotal}</span>
              </div>
              <Button onClick={submitSelfwash} disabled={saving} className="w-full rounded-full bg-bamboo hover:bg-bamboo-hover text-brand-cream font-bold h-11 mt-2" data-testid="pb-sw-submit">
                <Save className="h-4 w-4 mr-2" /> {saving ? p.saving : p.save_selfwash}
              </Button>
            </div>
          )}

          <p className="text-[11px] text-brand-ink/50 text-center italic pt-2">{p.email_note}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneBookingDialog;
